import { Dtos, formatAddress, SuretyBondStatus, SuretyQuoteStatus } from "@inrev/common";
import { type PDFViewerControl } from "@portal/components/patterns/pdf-viewer";
import { Button, Card, Text } from "@portal/components/primitives";
import { PDFViewerModal } from "@portal/components/ui/PDFViewerModal";
import { QueryKey } from "@tanstack/react-query";
import { ReactNode, useMemo, useState } from "react";
import { bondFormTypeLabelMap } from "../../../constants";
import { formatFromISODate, formatNameOption } from "../../../utils";
import { AdminDataList, AdminDataListProps } from "../../shared/admin/AdminDataList";

type AdminBondFormDto =
	| Dtos.Admin.SuretyBondForm.Get.Response
	| Dtos.Admin.SuretyBondForm.Issued.Get.Response;

type AdminBondFormCardProps = {
	bondForm: AdminBondFormDto;
	bondFormViewUrl: string;
	bondFormViewQueryKey: QueryKey;
	bottomContent?: ReactNode;
	status: SuretyQuoteStatus | SuretyBondStatus;
};

const sourceMap = {
	library: "Selected from the library",
	upload: "Uploaded by the agent",
};

export const AdminBondFormCard = ({
	bondForm,
	bondFormViewUrl,
	bondFormViewQueryKey,
	bottomContent,
	status,
}: AdminBondFormCardProps) => {
	const [pdfViewerControl, setPdfViewerControl] = useState<PDFViewerControl | undefined>();

	const data = useMemo(() => {
		const data: AdminDataListProps["data"] = [];
		data.push({ label: "Issuance Method", value: bondForm.issuanceMethod });
		data.push({
			label: "Source",
			value:
				bondForm.source === "upload" &&
				bondForm.issuanceMethod === undefined &&
				bondForm.uploadedFiles.length === 0
					? "--"
					: sourceMap[bondForm.source],
		});
		data.push({
			label: "Bond Type",
			value: bondForm.bondFormType ? bondFormTypeLabelMap[bondForm.bondFormType] : undefined,
		});
		data.push({ label: "Bond Number", value: bondForm.fillData.bondNumber });
		data.push({
			label: "Effective Date",
			value: formatFromISODate(bondForm.fillData.effectiveDate),
		});
		data.push({ label: "Obligee Name", value: bondForm.fillData.obligees?.[0].name });
		if (bondForm.fillData.obligees?.[0].address) {
			data.push({
				label: "Obligee Address",
				value: formatAddress(bondForm.fillData.obligees[0].address),
			});
		} else {
			data.push({ label: "Obligee Address" });
		}
		data.push({
			label: "Principal Signer Name",
			value: formatNameOption(bondForm.fillData.principalSigner),
		});
		data.push({ label: "Principal Signer Title", value: bondForm.fillData.principalSigner?.title });

		return data;
	}, [bondForm]);

	const canView =
		status === SuretyQuoteStatus.pending ||
		status === SuretyBondStatus.open ||
		status === SuretyBondStatus.closed;

	return (
		<>
			<Card
				elevation="raised"
				className="rounded-xl"
				data-testid="admin-bond-request-bond-form-details-card"
			>
				<Card.Body className="p-6">
					<div className="flex flex-col w-full space-y-5">
						<div className="flex items-center justify-between w-full">
							<Text type="title" color="primary">
								Bond Form Details
							</Text>
							{canView && (
								<Button
									variant="outline"
									color="secondary"
									size="sm"
									onClick={() =>
										setPdfViewerControl({
											fetchUrl: bondFormViewUrl,
											queryKey: bondFormViewQueryKey,
										})
									}
								>
									View
								</Button>
							)}
						</div>
						<div className="w-full flex flex-col space-y-6 divide-y divide-dashed divide-bc-default">
							<AdminDataList data={data} />
							{bottomContent}
						</div>
					</div>
				</Card.Body>
			</Card>
			<PDFViewerModal
				control={pdfViewerControl}
				onClose={() => setPdfViewerControl(undefined)}
				header="View Bond Form"
				trackingId="admin-bond-form-viewer"
			/>
		</>
	);
};
